import { useCallback, useContext, useEffect, useState } from 'react';
import { CheckoutLogin } from './checkout-login';
import { CheckoutLegal } from './checkout-legal';
import { CheckoutBilling } from './checkout-billing';
import { CheckoutPaymentSummary } from './checkout-payment-summary';
import { CheckoutCoupon } from './checkout-coupon';
import { CheckoutSummary } from './checkout-summary';
import { useCustomer } from '../../hooks/use-customer-context';
import { useCustomerAPI } from '../../clients/customer-api/customer-api-client';
import { Configuration } from '../../clients/quipli-service/configuration/configuration';
import { Label } from '../../clients/quipli-service/label/label';
import { Spinner } from '../../components/spinner/spinner';
import { CheckoutDelivery } from './checkout-delivery';
import { UserAddress, UserDocumentType } from '../../clients/quipli-service/user/user';
import { Location } from '../../clients/quipli-service/location/location';
import { OrderDocumentType, OrderSource, OrderStatus, OrderType } from '../../clients/quipli-service/order/order';
import {
  CouponLineItem,
  DamageWaiverLineItem,
  DeliveryLineItem,
  ExcludeProductFeeRequest,
  FeeLineItem,
  FinancialSummary,
  PickupLineItem,
  ProductFeeLineItem,
  ProductLineItem,
  SecurityDepositLineItem,
  TaxLineItem,
} from '../../clients/quipli-service/financials/financials';
import { CheckoutDocuments } from './checkout-documents';
import { TenantContext } from '../../context/tenant-context';
import Dinero, { Currency } from 'dinero.js';
import { debounce } from 'lodash';
import {
  Payment,
  PaymentMethod,
  PaymentMethodRequest,
  PaymentMethodType,
  toOrderPaymentMethod,
} from '../../clients/quipli-service/payment/payment';
import usePrevious from '../../hooks/use-previous';
import { Cart, RentalCartItem, SaleCartItem } from '../../clients/customer-api/cart/cart';
import { ecommerceToast } from '../../util/toasts';
import { isApiError } from '../../clients/axios-instance';
import { ProductCustomService, ProductOption } from '../../clients/customer-api/product/customer-product';
import { convertToMoney } from '../../util/util';
import { DateTime } from 'luxon';
import { CheckoutBacklink } from './checkout-backlink';
import { DeliveryOption, ShippingType } from '../../clients/quipli-service/delivery/delivery';
import { useCustomerYorentClient } from '../../clients/customer-api/customer-yorent-api-client';
import { cartToEcommerceItems, orderToPurchaseEvent } from '../../googleTagManager';
import { PaymentIntent } from '@stripe/stripe-js';
import { usePaymentApiV1 } from '../../clients/quipli-service/payment/payment-api-v1';

export enum StepAction {
  'Auth' = 0,
  'Shipping' = 1,
  'DeliveryAddress' = 2,
  'Billing' = 3,
  'Payment' = 4,
  'DriversLicense' = 5,
  'Insurance' = 6,
  'OtherDocument' = 7,
  'Legal' = 8,
}

type ProductCustomizations = { options: ProductOption[]; services: ProductCustomService[] };

export type CheckoutSubmitRequest = {
  paymentMethod: PaymentMethodRequest;
  billingAddressId?: number;
  deliveryAddressId?: number;
  deliveryType?: ShippingType;
  notes?: string;
  hasVipCoupon?: boolean;
  digitalSignatureDate?: DateTime;
};

export const CheckoutView = ({ locationId }: { locationId: number }) => {
  const { getCart, getLocationById, getProductCustomizations, placeOrder } = useCustomerAPI();
  const { refreshCartProductCount } = useCustomerYorentClient();
  const { completeAffirm } = usePaymentApiV1();
  const { contextPath } = useContext(TenantContext);
  const customer = useCustomer();

  const [location, setLocation] = useState<Location>();
  const [cart, setCart] = useState<Cart>();
  const [coupon, setCoupon] = useState<string>();
  const [excludeFeeIds, setExcludedFeeIds] = useState<number[]>([]);
  const [excludeProductFees, setExcludedProductFees] = useState<ExcludeProductFeeRequest[]>([]);
  const [excludeDamageWaiver, setExcludedDamageWaiver] = useState<boolean>(false);
  const [customizations, setCustomizations] = useState<Map<number, ProductCustomizations>>();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [paymentIntent, setPaymentIntent] = useState<PaymentIntent | undefined>();
  const [payment, setPayment] = useState<Payment | undefined>();

  const [summary, setSummary] = useState<FinancialSummary>();

  const init = async () => {
    const _location = await getLocationById(locationId);
    setLocation(_location);
    fetchCart();
  };

  const fetchCart = async () => {
    const _cart = await getCart(locationId);

    if (!_cart || _cart.items.length === 0) {
      window.location.href = `${contextPath}cart`;
      return;
    }

    const customizations = new Map<number, ProductCustomizations>();
    for (const item of _cart.items) {
      if (item.options || (item instanceof SaleCartItem && item.services)) {
        const customs = await getProductCustomizations(item.product.id);
        customizations.set(item.product.id, customs);
      }
    }
    setCustomizations(customizations);
    setCoupon(_cart.meta?.coupon);
    setExcludedProductFees(_cart.meta?.excludeProductFees || []);
    setExcludedFeeIds(_cart.meta?.excludeFeeIds || []);
    setExcludedDamageWaiver(_cart.meta?.excludeDamageWaiver ?? false);
    setCart(_cart);
    if (typeof window.dataLayer !== 'undefined') {
      window.dataLayer.push({
        event: 'begin_checkout',
        ecommerce: {
          items: cartToEcommerceItems(_cart),
        },
      });
    }
  };

  const onSubmit = async (req: CheckoutSubmitRequest) => {
    if (isSubmitting || !location || !summary || !cart) return;

    if (!req.deliveryType) {
      ecommerceToast('Please select a delivery method.', 'error');
      return;
    }

    setIsSubmitting(true);

    const cartTotal = summary.lineItems.reduce(
      (acc, line) => (line.type === 'Product' ? acc.add(line.amount) : acc),
      Dinero({ amount: 0, currency: location.currency as Currency }),
    );

    const orderDelivery = summary?.lineItems.find((line) => line.type === 'Delivery') as DeliveryLineItem | undefined;
    const delivery = orderDelivery
      ? orderDelivery.amount
      : summary?.lineItems.reduce(
          (acc, line) => (line.type === 'ProductDelivery' ? acc.add(line.amount) : acc),
          Dinero({ amount: 0, currency: location.currency as Currency }),
        );
    const orderPickup = summary?.lineItems.find((line) => line.type === 'Pickup') as PickupLineItem;
    const pickup = orderPickup
      ? orderPickup.amount
      : summary?.lineItems.reduce(
          (acc, line) => (line.type === 'ProductPickup' ? acc.add(line.amount) : acc),
          Dinero({ amount: 0, currency: location.currency as Currency }),
        );
    const damageWaiver = summary?.lineItems.find((line) => line.type === 'DamageWaiver') as
      | DamageWaiverLineItem
      | undefined;
    const couponDiscount =
      summary?.lineItems.find((line) => line.type === 'TagPricing' || line.type === 'Coupon')?.amount ||
      Dinero({ amount: 0, currency: location.currency as Currency });
    const tax = summary?.lineItems.find((line) => line.type === 'Tax') as TaxLineItem;
    const locationFees = (summary?.lineItems.filter((line) => line.type === 'Fee') || []) as FeeLineItem[];
    const discount =
      summary?.lineItems.find((line) => line.type === 'AdditionalDiscount')?.amount ??
      Dinero({ amount: 0, currency: location.currency as Currency });

    const orderSecurityDeposit = summary?.lineItems.find((line) => line.type === 'SecurityDeposit') as
      | SecurityDepositLineItem
      | undefined;

    const securityDepositPrice = orderSecurityDeposit
      ? orderSecurityDeposit.amount
      : summary?.lineItems.reduce(
          (acc, line) => (line.type === 'ProductSecurityDeposit' ? acc.add(line.amount) : acc),
          Dinero({ amount: 0, currency: location.currency as Currency }),
        );

    try {
      const order = await placeOrder({
        locationId: location.id,
        billingAddressId: req.billingAddressId,
        deliveryAddressId: req.deliveryAddressId,
        deliveryType: req.deliveryType,
        notes: req.notes,
        isCycleBilled: false,
        documentType: OrderDocumentType.Order,
        isAutoCharged: false,
        status: OrderStatus.Requested,
        source: OrderSource.Online,
        paymentMethod: req.paymentMethod,
        currency: summary.total.getCurrency(),
        cartTotal: cartTotal.getAmount(),
        damageWaiver: damageWaiver?.isSelected
          ? {
              amount: damageWaiver.amount.getAmount(),
              hasCustomAmount: damageWaiver.hasCustomAmount,
            }
          : undefined,
        pickup: pickup
          ? {
              amount: pickup?.getAmount() || 0,
              hasCustomAmount: orderPickup?.hasCustomAmount || false,
            }
          : undefined,
        delivery: delivery
          ? {
              amount: delivery?.getAmount() || 0,
              hasCustomAmount: orderDelivery?.hasCustomAmount || false,
            }
          : undefined,
        tax: {
          amount: tax.amount.getAmount(),
          hasCustomAmount: tax.hasCustomAmount,
          hasCustomPercentage: tax.hasCustomPercentage,
          percentage: tax.percentage,
          taxCode: tax.taxCodeId,
          breakdown: tax.taxBreakdown,
        },
        coupon: !couponDiscount.isZero()
          ? {
              code: (customer?.tagPricing ? customer.tagPricing.name : coupon)!,
              amount: couponDiscount.multiply(-1).getAmount(),
              isVip: req.hasVipCoupon === true,
            }
          : undefined,
        additionalFees: locationFees
          .filter((fee) => fee.isSelected)
          .map((fee) => ({
            feeId: fee.feeId,
            amount: fee.amount.getAmount(),
            hasCustomAmount: fee.hasCustomAmount,
          })),
        additionalDiscount: !discount.isZero() ? discount.multiply(-1).getAmount() : undefined,
        total: summary.total.getAmount(),
        googleCalculation: summary.googleCalculation,
        securityDeposit: !securityDepositPrice.isZero()
          ? {
              amount: securityDepositPrice.getAmount(),
              percentage: orderSecurityDeposit?.percentage || 0,
            }
          : undefined,
        products: cart.items.map((cartItem) => {
          const productLineItem = summary.lineItems.find(
            (line) => line.type === 'Product' && line.id === cartItem.id,
          )! as ProductLineItem;

          return {
            uniqueId: cartItem.id,
            productId: cartItem.product.id,
            type: cartItem.type,
            amount: productLineItem.unitPrice.getAmount(),
            hasCustomAmount: productLineItem.hasCustomAmount,
            pickup:
              summary.lineItems
                .find((line) => line.type === 'ProductPickup' && line.productLineItemId === cartItem.id)
                ?.amount?.getAmount() || 0,
            delivery:
              summary.lineItems
                .find((line) => line.type === 'ProductDelivery' && line.productLineItemId === cartItem.id)
                ?.amount?.getAmount() || 0,
            securityDeposit: summary.lineItems
              .find((line) => line.type === 'ProductSecurityDeposit' && line.productLineItemId === cartItem.id)
              ?.amount?.getAmount(),
            quantity: cartItem.quantity,
            additionalFees: summary.lineItems
              .filter((line) => line.type === 'ProductFee' && line.productLineItemId === cartItem.id && line.isSelected)
              .map((line) => {
                const fee = line as ProductFeeLineItem;
                return {
                  feeId: fee.productFeeId,
                  amount: fee.amount.getAmount(),
                  hasCustomAmount: fee.hasCustomAmount,
                };
              }),
            start: cartItem instanceof RentalCartItem ? cartItem.startDate : undefined,
            end: cartItem instanceof RentalCartItem ? cartItem.endDate : undefined,
            options: Object.entries(cartItem.options).map(([optionId, valueId]) => {
              const option = customizations
                ?.get(cartItem.product.id)
                ?.options.find((option) => option.id === +optionId);
              const name = option?.name;
              const value = option?.values!.find((v) => v.id === +valueId)!.name;

              return {
                id: +optionId,
                name: name || '',
                valueId: +valueId,
                value: value || '',
              };
            }),
            services:
              cartItem instanceof SaleCartItem
                ? cartItem.services.map(({ id, value }) => {
                    const service = customizations?.get(cartItem.product.id)?.services.find((s) => s.id === id);

                    return {
                      id,
                      name: service?.name || '',
                      value: value!,
                      price: convertToMoney(service?.price ?? 0, location.currency as Currency).getAmount(),
                    };
                  })
                : [],
            isCustom: false,
          };
        }),
        dateSigned: req.digitalSignatureDate?.toISO(),
      });
      if (typeof window.dataLayer !== 'undefined') {
        window.dataLayer.push(orderToPurchaseEvent(order, location.currency));
      }
      await refreshCartProductCount();

      if (order.paymentType === 'Affirm' && payment && paymentIntent) {
        await completeAffirm(order.id, payment.id);
        paymentIntent.next_action?.redirect_to_url &&
          window.open(paymentIntent.next_action.redirect_to_url.url!, '_blank');
      }

      window.location.href = `${contextPath}custom/payment_success`;
    } catch (error: any) {
      ecommerceToast(typeof error.message === 'string' ? error.message : 'Something went wrong.', 'error');

      // Do not rent
      if (isApiError(error) && error.status === 403) {
        window.location.href = `${contextPath}cart`;
      }

      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    init();
  }, [locationId]);

  return location && cart ? (
    <ProcessCheckout
      cart={cart}
      fetchCart={fetchCart}
      location={location}
      coupon={coupon}
      customizations={customizations}
      excludeFeeIds={excludeFeeIds}
      excludeProductFees={excludeProductFees}
      excludeDamageWaiver={excludeDamageWaiver}
      summary={summary}
      isSubmitting={isSubmitting}
      setCoupon={setCoupon}
      setExcludedFeeIds={setExcludedFeeIds}
      setExcludedProductFees={setExcludedProductFees}
      setExcludedDamageWaiver={setExcludedDamageWaiver}
      setSummary={setSummary}
      onSubmit={onSubmit}
      setPaymentIntent={setPaymentIntent}
      setPayment={setPayment}
    />
  ) : (
    <Spinner />
  );
};

export const ProcessCheckout = ({
  cart,
  fetchCart,
  location,
  coupon,
  customizations,
  excludeFeeIds,
  excludeProductFees,
  excludeDamageWaiver,
  summary,
  isSubmitting,
  setCoupon,
  setExcludedFeeIds,
  setExcludedProductFees,
  setExcludedDamageWaiver,
  setSummary,
  onSubmit,
  isQuote = false,
  setPaymentIntent,
  setPayment,
}: {
  cart: Cart;
  location: Location;
  coupon?: string;
  customizations?: Map<number, ProductCustomizations>;
  excludeFeeIds?: number[];
  excludeProductFees?: ExcludeProductFeeRequest[];
  excludeDamageWaiver?: boolean;
  summary?: FinancialSummary;
  isSubmitting: boolean;
  fetchCart: () => Promise<void>;
  setCoupon?: (coupon?: string) => void;
  setExcludedFeeIds?: (ids: number[]) => void;
  setExcludedProductFees?: (fees: ExcludeProductFeeRequest[]) => void;
  setExcludedDamageWaiver?: (isExcluded: boolean) => void;
  setSummary?: (summary: FinancialSummary) => void;
  onSubmit?: (req: CheckoutSubmitRequest) => void;
  isQuote?: boolean;
  setPaymentIntent?: (paymentIntent: PaymentIntent) => void;
  setPayment?: (payment: Payment) => void;
}) => {
  const customer = useCustomer();

  const { getConfigurations, getMultipleLabels, getUserAddresses, calculateFinancialSummary } = useCustomerAPI();

  let step = customer ? 1 : 2;
  const [currentStep, setCurrentStep] = useState<StepAction>(
    customer ? (isQuote ? StepAction.DeliveryAddress : StepAction.Shipping) : StepAction.Auth,
  );

  const [notes, setNotes] = useState<string>('');
  const [addresses, setAddresses] = useState<UserAddress[]>([]);
  const [billingAddress, setBillingAddress] = useState<UserAddress>();
  const [deliveryAddress, setDeliveryAddress] = useState<UserAddress>();
  const [selectedDelivery, setSelectedDelivery] = useState<DeliveryOption>();
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>();
  const [visibleDocuments, setVisibleDocuments] = useState<UserDocumentType[]>([]);
  const [digitalSignature, setDigitalSignature] = useState<{ signature: string; date: DateTime }>();
  const [isSummaryLoading, setIsSummaryLoading] = useState<boolean>(false);
  const hasVipCoupon =
    (summary?.lineItems.find((li) => li.type === 'Coupon') as CouponLineItem | undefined)?.isVip === true;
  const prevHasVipCoupon = usePrevious(hasVipCoupon);

  // Conf
  const [conf, setConf] = useState<Configuration[]>();
  const [labels, setLabels] = useState<Label[]>([]);

  const fetchAddresses = async () => {
    if (!customer) return;

    const _addresses = await getUserAddresses();
    setAddresses(_addresses);

    if (cart.meta?.selectedAddresses?.billing) {
      setBillingAddress(_addresses.find((a) => a.id === cart.meta?.selectedAddresses?.billing));
    }

    if (cart.meta?.selectedAddresses?.shipping) {
      setDeliveryAddress(_addresses.find((a) => a.id === cart.meta?.selectedAddresses?.shipping));
    }
  };

  const fetchFinancialSummary = useCallback(
    debounce(async () => {
      setIsSummaryLoading(true);
      const _summary = await calculateFinancialSummary({
        locationId: location!.id,
        couponCode: coupon,
        billingAddressId: billingAddress?.id,
        deliveryAddressId: deliveryAddress?.id,
        deliveryType: selectedDelivery?.type,
        taxRateId: customer?.defaultTaxCodeId,
        items: cart!.items.map((item) => ({
          id: item.id,
          ...(item instanceof RentalCartItem
            ? {
                start: item.startDate,
                end: item.endDate,
                type: OrderType.Rental,
              }
            : {
                type: OrderType.Sale,
                isCustom: false,
                services: (item as SaleCartItem).services,
              }),
          productId: item.product.id,
          quantity: item.quantity,
        })),
        paymentMethod: toOrderPaymentMethod(paymentMethod),
        excludeFeeIds,
        excludeProductFees,
        excludeDamageWaiver,
        documentType: OrderDocumentType.Order,
      });

      setSummary?.(_summary);
      setIsSummaryLoading(false);
    }, 500),
    [
      cart,
      location,
      coupon,
      deliveryAddress,
      selectedDelivery,
      conf,
      excludeFeeIds,
      excludeProductFees,
      excludeDamageWaiver,
      paymentMethod,
    ],
  );

  const submit = () => {
    if (isSummaryLoading || !customer || !location || !summary || !cart) return;

    let paymentMethodRequest: PaymentMethodRequest;
    switch (paymentMethod?.type) {
      case PaymentMethodType.Stripe:
        paymentMethodRequest = {
          type: PaymentMethodType.Stripe,
          cardId: paymentMethod.card?.id,
          bankAccountId: paymentMethod.bankAccount?.id,
          method: paymentMethod.method,
        };
        break;
      case PaymentMethodType.OnAccount:
        paymentMethodRequest = {
          type: PaymentMethodType.OnAccount,
          accountNumber: paymentMethod.account.accountNumber,
          poNumber: paymentMethod?.poNumber,
        };
        break;
      case PaymentMethodType.Cash:
        paymentMethodRequest = { type: PaymentMethodType.Cash };
        break;
      case PaymentMethodType.Check:
        paymentMethodRequest = { type: PaymentMethodType.Check };
        break;
      case PaymentMethodType.None:
      default:
        paymentMethodRequest = { type: PaymentMethodType.None };
        break;
    }

    onSubmit?.({
      paymentMethod: paymentMethodRequest,
      billingAddressId: billingAddress?.id,
      deliveryAddressId: deliveryAddress?.id,
      deliveryType: location.allowDeliverySelection ? selectedDelivery?.type : ShippingType.SelfPickUp,
      notes,
      hasVipCoupon,
      digitalSignatureDate: digitalSignature?.date,
    });
  };

  const findCorrectStep = useCallback(() => {
    if (!location) return;

    if (hasVipCoupon !== prevHasVipCoupon) {
      setCurrentStep(
        location.allowDeliverySelection
          ? isQuote
            ? StepAction.DeliveryAddress
            : StepAction.Shipping
          : hasVipCoupon
          ? StepAction.Payment
          : StepAction.Billing,
      );
      return;
    }

    if (currentStep === StepAction.Shipping && !location.allowDeliverySelection) {
      setCurrentStep(hasVipCoupon ? StepAction.Payment : StepAction.Billing);
      return;
    }

    if (
      currentStep === StepAction.DeliveryAddress &&
      isQuote &&
      cart.meta?.selectedAddresses?.shipping !== undefined &&
      !!deliveryAddress
    ) {
      setCurrentStep(StepAction.Billing);
      return;
    }

    if (
      currentStep === StepAction.Billing &&
      (hasVipCoupon || (isQuote && cart.meta?.selectedAddresses?.billing !== undefined && !!billingAddress))
    ) {
      setCurrentStep(StepAction.Payment);
      return;
    }

    if (currentStep === StepAction.Payment && location?.allowNoPayment && !hasVipCoupon) {
      setCurrentStep(StepAction.DriversLicense);
      return;
    }

    if (currentStep === StepAction.DriversLicense && hasVipCoupon) {
      setCurrentStep(StepAction.Legal);
      return;
    }

    if (currentStep === StepAction.DriversLicense && !visibleDocuments.includes(UserDocumentType.License)) {
      if (visibleDocuments.includes(UserDocumentType.Insurance)) {
        setCurrentStep(StepAction.Insurance);
        return;
      }

      if (visibleDocuments.includes(UserDocumentType.OtherCheckout)) {
        setCurrentStep(StepAction.OtherDocument);
        return;
      }

      setCurrentStep(StepAction.Legal);
      return;
    }

    if (currentStep === StepAction.Insurance && !visibleDocuments.includes(UserDocumentType.Insurance)) {
      if (visibleDocuments.includes(UserDocumentType.OtherCheckout)) {
        setCurrentStep(StepAction.OtherDocument);
        return;
      }

      setCurrentStep(StepAction.Legal);
      return;
    }

    if (currentStep === StepAction.OtherDocument && !visibleDocuments.includes(UserDocumentType.OtherCheckout)) {
      setCurrentStep(StepAction.Legal);
      return;
    }
  }, [currentStep, hasVipCoupon, prevHasVipCoupon, location, visibleDocuments, deliveryAddress, billingAddress]);

  useEffect(() => {
    const fetchData = async () => {
      // Conf
      setConf(
        await getConfigurations([
          'CONF_ENABLE_AFFIRM',
          'CONF_RENT_ON_ACCOUNT',
          'CONF_STRIPE_TERMINAL',
          'CONF_STRIPE_TERMINAL_RENTER',
          'CONF_REQUIRED_ORDER_NOTES',
          'CONF_ORDER_NOTES_PLACEHOLDER',
          'CONF_QUIPLI_BACKLINKS',
          'CONF_QUIPLI_BACKLINK_URL',
          'CONF_ENABLE_GUEST_CHECKOUT',
          'CONF_DISABLE_USER_REGISTRATION',
          'CONF_RENTER_LIC_INFO',
          'CONF_PROOF_OF_INSURANCE',
          'CONF_PROOF_INSURANCE_MANDATORY',
          'CONF_OTHER_DOC_UPLOAD',
          'CONF_OTHER_DOC_REQUIRED',
          'CONF_DIGITAL_SIGNATURE',
          'CONF_DELIVERY_LOCATIONS_MODULE',
          'CONF_USER_UNAPPROVED_HIDE_PRICES',
          'CONF_COUPON_CODE',
          'CONF_PRODUCT_IMAGES_V2',
          'CONF_DISPLAY_JURISDICTION_TAXES_BREAKDOWN',
          'CONF_IDSCAN_LICENSE_KEY',
        ]),
      );
      // Labels
      setLabels(
        await getMultipleLabels([
          'L_DELIVERY_BLOCK_TITLE',
          'LBL_CUSTOM_DOC_UPLOAD',
          'L_ORDER_NOTES_BLOCK_TITLE',
          'L_QUIPLI_BACKLINK_TEXT',
          'L_CHECKOUT_TEXT',
          'L_PLACE_ORDER_REQUEST',
          'M_ELECTRONIC_CONSENT',
          'L_ORDER',
          'M_Pickup_at',
          'L_CHECKOUT_PROOF_INSURANCE',
        ]),
      );
      // Addresses
      await fetchAddresses();
      // Cart
      // await fetchCart();
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (cart && location && conf && !isQuote) {
      if (conf.find((c) => c.variable === 'CONF_USER_UNAPPROVED_HIDE_PRICES')?.value === '1' && !customer) {
        setSummary?.({
          total: Dinero({ amount: 0, currency: location.currency as Currency }),
          lineItems: [],
        });
        return;
      }

      fetchFinancialSummary();
    }
  }, [
    cart,
    location,
    coupon,
    deliveryAddress,
    selectedDelivery,
    conf,
    excludeFeeIds,
    excludeProductFees,
    excludeDamageWaiver,
    paymentMethod,
    isQuote,
  ]);

  useEffect(() => {
    findCorrectStep();
  }, [currentStep, hasVipCoupon, prevHasVipCoupon, location, visibleDocuments, deliveryAddress, billingAddress]);

  return !cart || !location || !conf || !summary ? (
    <Spinner />
  ) : (
    <div className="grid grid-cols-1 xxl:grid-cols-12 gap-2 xxl:gap-8">
      <div className="order-2 xxl:!order-1 xxl:col-span-8 flex flex-col gap-2">
        {!customer && <CheckoutLogin />}

        {location.allowDeliverySelection && (
          <CheckoutDelivery
            title={labels?.find((l) => l.key === 'L_DELIVERY_BLOCK_TITLE')?.caption || 'Delivery Summary'}
            currentStep={currentStep}
            step={step++}
            addresses={addresses.filter((a) => a.type === 'delivery')}
            cartItems={cart?.items || []}
            location={location}
            reload={fetchCart}
            setCurrentstep={setCurrentStep}
            onSelectAddress={(address) => setDeliveryAddress(address)}
            selectedAddress={deliveryAddress}
            reloadAddresses={fetchAddresses}
            isSubmitting={isSubmitting || isSummaryLoading}
            isQuote={isQuote}
            showSelectedAddress={isQuote && cart.meta?.selectedAddresses?.shipping !== undefined && !!deliveryAddress}
            selectedDelivery={selectedDelivery}
            setSelectedDelivery={setSelectedDelivery}
            useImageV2={conf.find((c) => c.variable === 'CONF_PRODUCT_IMAGES_V2')?.value === '1'}
          />
        )}

        {!hasVipCoupon && (
          <CheckoutBilling
            currentStep={currentStep}
            step={step++}
            addresses={addresses.filter((a) => a.type === 'billing')}
            setCurrentstep={setCurrentStep}
            onSelect={(address) => setBillingAddress(address)}
            selectedAddress={billingAddress}
            reload={fetchAddresses}
            location={location}
            isSubmitting={isSubmitting || isSummaryLoading}
            showSelectedAddress={isQuote && cart.meta?.selectedAddresses?.billing !== undefined && !!billingAddress}
          />
        )}

        {(!location.allowNoPayment || hasVipCoupon) && (
          <CheckoutPaymentSummary
            currentStep={currentStep}
            step={step++}
            isSubmitting={isSubmitting || isSummaryLoading}
            amount={summary?.total.toUnit() || 0}
            setCurrentStep={setCurrentStep}
            paymentMethod={paymentMethod}
            setPaymentMethod={setPaymentMethod}
            address={billingAddress}
            setPaymentIntent={setPaymentIntent}
            setPayment={setPayment}
          />
        )}

        {cart && !hasVipCoupon && (
          <CheckoutDocuments
            currentStep={currentStep}
            step={step}
            conf={conf}
            labels={labels}
            isRental={cart.items.every((item) => item.type === OrderType.Rental)}
            categoryIds={cart.items.flatMap((i) => i.product.categoryIds || [])}
            isSubmitting={isSubmitting || isSummaryLoading}
            setCurrentStep={setCurrentStep}
            setVisibleDocuments={setVisibleDocuments}
          />
        )}

        <CheckoutLegal
          currentStep={currentStep}
          conf={conf}
          labels={labels}
          notes={notes}
          setNotes={setNotes}
          digitalSignature={digitalSignature}
          setDigitalSignature={setDigitalSignature}
          isSubmitting={isSubmitting || isSummaryLoading}
          onPlaceOrder={submit}
          isQuote={isQuote}
        />

        <CheckoutBacklink conf={conf} labels={labels} />
      </div>

      <div className="order-1 xxl:!order-2 xxl:col-span-4 flex flex-col gap-2 sticky self-start xxl:top-[calc(var(--header-offset-top)_+_20px)]">
        {!isQuote && conf.find((c) => c.variable === 'CONF_COUPON_CODE')?.value === '1' && (
          <CheckoutCoupon
            coupon={
              summary?.lineItems.find((li) => li.type === 'TagPricing' || li.type === 'Coupon') as
                | CouponLineItem
                | undefined
            }
            cart={cart}
            location={location}
            setCoupon={setCoupon}
            isSubmitting={isSubmitting || isSummaryLoading}
          />
        )}

        {cart && summary && location ? (
          <CheckoutSummary
            cart={cart}
            summary={summary}
            customizations={customizations}
            location={location}
            config={conf}
            labels={labels || []}
            reload={fetchCart}
            isSubmitting={isSubmitting || isSummaryLoading}
            excludeFeeIds={excludeFeeIds}
            excludeProductFees={excludeProductFees}
            excludeDamageWaiver={excludeDamageWaiver}
            setExcludedFeeIds={setExcludedFeeIds}
            setExcludedProductFees={setExcludedProductFees}
            setExcludedDamageWaiver={setExcludedDamageWaiver}
            isQuote={isQuote}
          />
        ) : (
          <Spinner />
        )}
      </div>
    </div>
  );
};
